<template>
  <div class="stars">
    <div
      v-if="showRatingValue && hasRating"
      class="stars__value"
      :class="[
        `stars__value--${sizeValue}`
      ]"
    >
      {{ rating.toFixed(1) }}
    </div>

    <div
      class="stars__wrapper"
      :class="[
        `stars--${size}`
      ]"
    >
      <div
        class="stars__icon-wrapper"
        :style="{ cursor: selectable ? 'pointer' : '' }"
        @mouseout="mouseOverRating = 0"
      >
        <span
          v-for="item in 5"
          :key="item"
          class="stars__icon"
          :class="[
            `${getIconConfig(item)}`,
            {
              'stars__icon--disabled': !hasRating,
              'stars__icon--selectable': selectable
            }
          ]"
          @click="onSelect(item)"
          @touchdown="onSelect(item)"
          @mouseover="mouseOverRating = item"
        />
      </div>

      <span
        v-if="selectable && !hideSelectableDescription"
        class="stars__description"
      >{{
        ratingDescription
      }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
type Props = {
  /**
   * `extra-large`, large`, `normal`, `small`, `extra-small`
   * `extra-large-mobile-large` = extra-large on mobile and large on desktop
   * `small-mobile` = small on mobile and normal on desktop
   */
  size?: 'extra-large' | 'extra-large-mobile-large' | 'large' | 'large-mobile' | 'medium' | 'normal' | 'small' | 'small-mobile'
  sizeValue?: 'normal' | 'small' | 'small-mobile'
  rating?: number
  showRatingValue?: boolean
  selectable?: boolean
  hideSelectableDescription?: boolean
}

type Emits = {
  (eventName: 'select', value: number): void
}

const props = withDefaults(defineProps<Props>(), {
  size: 'normal',
  sizeValue: 'normal',
  rating: 0,
  showRatingValue: false,
  selectable: false,
  hideSelectableDescription: false
})

const emit = defineEmits<Emits>()

const i18n = useI18n()

const mouseOverRating = shallowRef(0)

const hasRating = computed(() => {
  return props.rating > 0
})

const ratingDescription = computed(() => {
  const rating: number = mouseOverRating.value !== 0
    ? mouseOverRating.value
    : Math.ceil(props.rating)

  switch (rating) {
    case 0:
      return i18n.t('reviews.empty_rating')
    case 1:
      return i18n.t('reviews.terrible')
    case 2:
      return i18n.t('reviews.bad')
    case 3:
      return i18n.t('reviews.passably')
    case 4:
      return i18n.t('reviews.good')
    case 5:
      return i18n.t('reviews.perfect')
    default:
      return i18n.t('reviews.empty_rating')
  }
})

const getIconConfig = (index: number) => {
  const value = index - 1
  let difference: number

  if (props.selectable && mouseOverRating.value !== 0) {
    difference = mouseOverRating.value - value

    return difference > 0
      ? 'icon-star-solid'
      : 'icon-star'
  }

  difference = props.rating - value

  if (difference < 0.25) {
    return 'icon-star'
  } else if (difference <= 0.75) {
    return 'icon-star-half-alt'
  } else {
    return 'icon-star-solid'
  }
}

const onSelect = (value: number) => {
  if (!props.selectable) {
    return
  }
  const emitValue = value === props.rating ? 0 : value

  emit('select', emitValue)
}
</script>

<style lang="scss" scoped>
.stars {
  $root: &;
  font-size: 1.2rem;

  &--small {
    @media only screen and (max-width: 360px) {
      font-size: 1rem;
    }
  }

  &--small-mobile {
    @media only screen and (max-width: 400px) {
      font-size: 1rem;
    }
    @media only screen and (min-width: 740px) {
      font-size: 1.6rem;
    }
  }

  &--normal {
    font-size: 1.6rem;
  }

  &--medium {
    font-size: 1.8rem;
  }

  &--large {
    font-size: 2.2rem;
    #{$root}__icon {
      padding-right: 1.2rem;
    }
  }

  &--large-mobile {
    @media only screen and (max-width: 740px) {
      font-size: 2.2rem;
      #{$root}__icon {
        padding-right: 1.2rem;
      }
    }
    @media only screen and (min-width: 740px) {
      font-size: 1.6rem;
    }
  }

  &--extra-large {
    font-size: 2.7rem;
    #{$root}__icon {
      padding-right: 0.5rem;
    }
  }

  &--extra-large-mobile-large {
    @media only screen and (max-width: 740px) {
      font-size: 2.7rem;
      #{$root}__icon {
        padding-right: 0.5rem;
      }
    }
    @media only screen and (min-width: 740px) {
      font-size: 2.2rem;
      #{$root}__icon {
        padding-right: 1.2rem;
      }
    }
  }

  &__value {
    font-weight: bold;
    margin-bottom: 0.4rem;
    margin-right: 0.7rem;
    &--normal {
      font-size: $h1-font-size;
      line-height: $h1-line-height;
    }

    &--small,
    &--small-mobile {
      font-size: $font-size;
      line-height: $line-height-16;
    }

    @media only screen and (min-width: 740px) {
      &--small-mobile {
        font-size: $h1-font-size;
        line-height: $h1-line-height;
      }
    }
  }

  &__wrapper {
    display: flex;
    align-items: center;
  }

  &__icon-wrapper {
    display: flex;

    &:hover .stars__icon {
      &--selectable {
        color: $primary-color;
      }
    }
  }

  &__description {
    margin-left: 1.6rem;
    color: $secondary-font-color;
    font-size: 1.4rem;
    line-height: $line-height-18;
  }

  &__icon {
    padding-right: 0.2rem;
    color: $primary-color;

    &--disabled {
      color: $dark-gray-color;
    }

    &:last-child {
      padding-right: 0;
    }
  }
}
</style>
